/**
 * Retry Handler
 * Handles retry logic for retryable errors
 */

import type { AxiosInstance, AxiosError } from "axios";
import { isRetryableError } from "../../errors";
import type {
  ExtendedAxiosRequestConfig,
  InterceptorConfig,
  RetryConfig,
} from "../../types";
import { shouldLog } from "../../utils/logging";
import { calculateRetryDelay } from "../../utils/retry";
import { DEFAULT_RETRY_CONFIG } from "../../utils/constants";

/**
 * Handles retry logic for retryable errors
 */
export async function handleRetry(
  error: AxiosError,
  originalRequest: ExtendedAxiosRequestConfig,
  axiosInstance: AxiosInstance,
  config: InterceptorConfig,
): Promise<unknown> {
  const retry: RetryConfig = config.retry || DEFAULT_RETRY_CONFIG;

  if (
    retry.count > 0 &&
    isRetryableError(error, [...retry.statusCodes]) &&
    (!originalRequest._retryCount || originalRequest._retryCount < retry.count)
  ) {
    originalRequest._retryCount = (originalRequest._retryCount || 0) + 1;

    const delay = calculateRetryDelay(originalRequest._retryCount, retry.delay);

    if (shouldLog(config)) {
      console.log(
        `[API] Retrying request (${originalRequest._retryCount}/${retry.count}) after ${delay}ms...`,
      );
    }

    if (config.metrics?.onRequestRetry) {
      config.metrics.onRequestRetry(
        originalRequest._retryCount,
        error.response?.status || 0,
      );
    }

    await new Promise<void>((resolve) => setTimeout(resolve, delay));

    return axiosInstance.request(originalRequest);
  }

  return null; // Indicates retry was not applicable
}
